import { Checkbox, Flex, InputNumber, Radio, Select, Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type {
  StatusAutomationParamSchema,
  StatusAutomationParams,
  StatusAutomationRuleDefinition,
  StatusAutomationRuleSetting
} from "../../../api/status-automation-settings.services.ts"
import { isAvailable, modesFor, ruleCopyKey } from "./status-automation-form.ts"

const { Text } = Typography

const P = "settingsConfiguration.statusAutomation.params"

export const MEETING_CATEGORIES = [
  "LEAD_MEETING",
  "SITE_VISIT",
  "INSPECTION"
] as const

const TRIGGERS_BY_RULE: Record<string, string[]> = {
  "lead.won": ["contract_signed", "esign_completed", "either"],
  "project.in_progress": [
    "first_milestone_started",
    "planned_start_reached",
    "either"
  ]
}

const STALE_ACTIONS = ["follow_up_flag", "nurturing_suggestion"]

// The catalog's schema wins over the built-in lists, so a value the backend
// adds or retires reaches the picker without a frontend change.
const choices = (
  schema: StatusAutomationParamSchema | undefined,
  fallback: readonly string[]
): string[] => schema?.enum ?? schema?.items?.enum ?? [...fallback]

interface ParamsProps {
  ruleKey: string
  schema: Record<string, StatusAutomationParamSchema>
  params: StatusAutomationParams
  disabled: boolean
  onChange: (params: StatusAutomationParams) => void
}

function RuleParams({
  ruleKey,
  schema,
  params,
  disabled,
  onChange
}: ParamsProps) {
  const { t } = useTranslation()
  const set = (key: string, value: unknown) =>
    onChange({ ...params, [key]: value })

  if (ruleKey === "lead.booked") {
    return (
      <Checkbox.Group
        aria-label={t(`${P}.meetingTypes`)}
        disabled={disabled}
        value={(params.meeting_categories as string[] | undefined) ?? []}
        onChange={(value) => set("meeting_categories", value)}
        options={choices(schema.meeting_categories, MEETING_CATEGORIES).map(
          (category) => ({
            value: category,
            label: t(`${P}.meetingCategories.${category}`, {
              defaultValue: category
            })
          })
        )}
      />
    )
  }

  const triggers = TRIGGERS_BY_RULE[ruleKey]
  if (triggers) {
    const label =
      ruleKey === "lead.won" ? t(`${P}.wonTrigger`) : t(`${P}.inProgressTrigger`)
    return (
      <Flex align="center" gap={8} wrap>
        <Text type="secondary">{label}</Text>
        <Select
          aria-label={label}
          className="min-w-56"
          disabled={disabled}
          value={params.trigger as string | undefined}
          onChange={(value) => set("trigger", value)}
          options={choices(schema.trigger, triggers).map((trigger) => ({
            value: trigger,
            label: t(`${P}.triggers.${trigger}`, { defaultValue: trigger })
          }))}
        />
      </Flex>
    )
  }

  if (ruleKey === "lead.stale") {
    return (
      <Flex align="center" gap={8} wrap>
        <Text type="secondary">{t(`${P}.staleDays`)}</Text>
        <InputNumber<number>
          aria-label={t(`${P}.staleDays`)}
          min={schema.days?.minimum ?? 1}
          max={schema.days?.maximum ?? 365}
          precision={0}
          disabled={disabled}
          value={params.days as number | undefined}
          onChange={(value) => {
            if (typeof value === "number") set("days", value)
          }}
          className="w-24"
        />
        <Text type="secondary">{t(`${P}.staleAction`)}</Text>
        <Select
          aria-label={t(`${P}.staleAction`)}
          className="min-w-52"
          disabled={disabled}
          value={params.action as string | undefined}
          onChange={(value) => set("action", value)}
          options={choices(schema.action, STALE_ACTIONS).map((action) => ({
            value: action,
            label: t(`${P}.actions.${action}`, { defaultValue: action })
          }))}
        />
      </Flex>
    )
  }

  return null
}

interface Props {
  definition: StatusAutomationRuleDefinition
  setting: StatusAutomationRuleSetting
  disabled: boolean
  note?: string
  errors?: string[]
  onChange: (setting: StatusAutomationRuleSetting) => void
}

export function StatusAutomationRuleRow({
  definition,
  setting,
  disabled,
  note,
  errors,
  onChange
}: Props) {
  const { t } = useTranslation()
  const copy = ruleCopyKey(definition.key)
  const name = t(`${copy}.name`, { defaultValue: definition.key })
  const available = isAvailable(definition)
  const locked = disabled || !available

  return (
    <div
      role="group"
      aria-label={name}
      className="flex flex-col gap-2 py-3 border-b border-solid border-0 border-gray-100 last:border-b-0"
    >
      <Flex justify="space-between" align="start" gap={12} wrap>
        <div className="flex flex-col min-w-0">
          <Flex align="center" gap={8} wrap>
            <Text strong>{name}</Text>
            {available ? null : (
              <Tag bordered={false}>
                {t("settingsConfiguration.statusAutomation.comingSoon")}
              </Tag>
            )}
          </Flex>
          <Text type="secondary">
            {t(`${copy}.when`, { defaultValue: "" })}
          </Text>
          {note ? <Text type="secondary" italic>{note}</Text> : null}
          {available ? null : (
            <Text type="secondary" italic>
              {t("settingsConfiguration.statusAutomation.comingSoonHint")}
            </Text>
          )}
        </div>
        <Radio.Group
          aria-label={t("settingsConfiguration.statusAutomation.modeLabel", {
            rule: name
          })}
          optionType="button"
          buttonStyle="solid"
          disabled={locked}
          value={setting.mode}
          onChange={(event) =>
            onChange({ ...setting, mode: event.target.value })
          }
          options={modesFor(definition).map((mode) => ({
            value: mode,
            label: t(`settingsConfiguration.statusAutomation.modes.${mode}`)
          }))}
        />
      </Flex>
      {setting.mode !== "off" && available ? (
        <RuleParams
          ruleKey={definition.key}
          schema={definition.params_schema ?? {}}
          params={setting.params}
          disabled={disabled}
          onChange={(params) => onChange({ ...setting, params })}
        />
      ) : null}
      {errors?.length ? (
        <Text type="danger" role="alert">
          {errors.join(" ")}
        </Text>
      ) : null}
    </div>
  )
}
